import { memo, useState } from "react";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ExceptionTooltipContent, ExceptionType } from "@/features/exception-logs";
import {
  ExceptionCounts,
  NoShowGroup,
  RescheduleExceptionEntry,
  groupRhReschedulesByProfessional,
} from "@/features/users/utils/user-calculations";
import {
  ProductPaymentBreakdownEntry,
  ProductRefund,
  ProductSummary,
} from "@/features/users/utils/product-summary";
import { PaymentType } from "@/pages/users/types/user";
import { CompletedSessionProfessional } from "@/features/users/utils/past-professionals";
import { useHoverOpen } from "@/hooks/use-hover-open";
import { cn } from "@/lib/utils";
import { REFUND_STATUS_LABELS, formatMinorUnits } from "./refunds-summary";

interface UserSummaryCardRedesignedProps {
  productSummaries: ProductSummary[];
  sessionsSummary: {
    total: number;
    used: number;
  };
  paymentSummary: Record<string, number>;
  closestToExpiry?: {
    remaining: number;
    daysUntilExpiry: number;
  } | null;
  exceptionCounts: ExceptionCounts;
  rhReschedules: RescheduleExceptionEntry[];
  noShows: NoShowGroup[];
}

// Takes MAJOR units (rupees) — calculatePaymentSummary / product-summary have
// already divided the paise payload by 100. The paise-taking sibling is
// formatMinorUnits in refunds-summary.tsx.
function formatMajorUnits(currency: string, amount: number) {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // A malformed currency code off the payload throws RangeError; with no
    // error boundary above, that would blank the whole Summary card. Degrade
    // to one plain line instead.
    return `${currency || ""} ${Math.round(amount).toLocaleString("en-IN")}`.trim();
  }
}

// Per-category tile themes from the design mock (2026-08-28): pastel surface +
// dark value text on the root, with the muted label tint and divider tint as
// CSS variables so TileRow/TilePager/dividers pick them up per tile.
// Exceptions, RH reschedules, and No-shows share the warm-cream neutral.
// Label tints hold WCAG AA (4.5:1) against their surface at text-xs.
const TILE_THEME = {
  sessions:
    "bg-[#EBF3FC] text-[#1A3A5C] [--tile-label:#4A6B93] [--tile-divider:#D3E2F2]",
  payments:
    "bg-[#E9F7EE] text-[#1D4A30] [--tile-label:#427958] [--tile-divider:#CBE8D5]",
  neutral:
    "bg-[#FFF7F0] text-[#2B1D14] [--tile-label:#8A5A3B] [--tile-divider:#EAD9C8]",
} as const;

// Terracotta for money leaving (refund amounts) — fixed regardless of the
// tile theme it sits on.
const REFUND_AMOUNT_CLASS = "text-[#B6452B]";

function Tile({
  title,
  headline,
  compact,
  children,
  themeClassName = TILE_THEME.neutral,
}: {
  title: string;
  headline: React.ReactNode;
  // Half-height card in the stacked last column: no full-tile min-h floor;
  // flex-1 makes the two stacked cards split the column evenly.
  compact?: boolean;
  children?: React.ReactNode;
  // One of TILE_THEME: surface + value-text colors plus the --tile-label /
  // --tile-divider variables consumed by TileRow, TilePager, and the section
  // dividers. Defaults to the warm-cream neutral.
  themeClassName?: string;
}) {
  return (
    // min-h reserves space for a full ROWS_PER_PAGE page + pager, so the card
    // keeps one stable height no matter how few rows the current page has
    // (grid rows are content-sized, so without this the tallest tile — and
    // with it the whole card — would resize on every page flip).
    <div
      className={cn(
        "rounded-lg flex flex-col",
        compact ? "flex-1 min-h-0 p-3" : "h-full min-h-[240px] p-3",
        themeClassName,
      )}
    >
      <div
        className={`font-medium text-[color:var(--tile-label)] ${compact ? "text-xs mb-0.5" : "text-sm mb-1"}`}
      >
        {title}
      </div>
      <div
        className={`flex items-center gap-2 flex-wrap font-bold ${compact ? "text-xl" : "text-2xl"}`}
      >
        {headline}
      </div>
      {children}
    </div>
  );
}

// Light categorical hues for the payment split bar (design ask: soft, light
// theme). Inter-segment CVD separation and the normal-vision floor PASS —
// both are hue-based comparisons between segments, independent of the tile
// surface. Against the payments surface (#E9F7EE) segment contrast sits in
// the relief band (~1.3–1.7:1) — covered by the 2px segment gaps, legend
// dots, numeric rows, and per-segment tooltips.
const SEGMENT_PALETTE = ["#67c8fa", "#5fe0a8", "#fcd34d", "#f8a0ce"] as const;

// Dynamic colors for products beyond the fixed palette — every product gets
// its own hue (no shared "Other" bucket). Golden-angle walk keeps successive
// hues far apart; the orange band (~10–80°) is skipped so a generated color
// never blends into the tile surface. Light variant to match the palette.
function generatedProductColor(overflowIndex: number): string {
  let hue = (200 + overflowIndex * 137.5) % 360;
  if (hue > 10 && hue < 80) {
    hue = (hue + 90) % 360;
  }
  return `hsl(${Math.round(hue)}, 72%, 72%)`;
}

// Color follows the entity, never its rank: the core product lines own fixed
// slots, so Psychiatry is the same blue on every user's card regardless of
// purchase order. First match wins; a product whose slot is already taken on
// this card (e.g. two counselling variants) falls through to a free slot.
const PRODUCT_COLOR_RULES: [RegExp, string][] = [
  [/psychiatr/i, SEGMENT_PALETTE[0]],
  [/counsel/i, SEGMENT_PALETTE[1]],
  [/clinical/i, SEGMENT_PALETTE[2]],
  [/couple/i, SEGMENT_PALETTE[3]],
];

// Product → color for one card: brand-named products claim their fixed slot
// first, remaining products take the unused slots in first-seen order, and
// once all slots are taken each further product gets its own generated hue —
// every product on the card has a unique color.
function assignPaymentColors(
  products: ProductSummary[],
): Map<string, string> {
  const colorByKey = new Map<string, string>();
  const used = new Set<string>();

  for (const product of products) {
    const rule = PRODUCT_COLOR_RULES.find(([pattern]) =>
      pattern.test(product.productName),
    );
    if (rule && !used.has(rule[1])) {
      colorByKey.set(product.productKey, rule[1]);
      used.add(rule[1]);
    }
  }

  let overflowIndex = 0;
  for (const product of products) {
    if (colorByKey.has(product.productKey)) continue;
    const free = SEGMENT_PALETTE.find((color) => !used.has(color));
    const color = free ?? generatedProductColor(overflowIndex++);
    colorByKey.set(product.productKey, color);
    used.add(color);
  }

  return colorByKey;
}

interface PaymentSegment {
  key: string;
  name: string;
  color: string;
  netTotal: number;
  amountLabel: string;
}

// One 100%-stacked horizontal bar: each product's share of the user's net
// payments. 2px gaps let the tile surface separate segments; each segment
// carries a hover tooltip (name · amount · share).
function PaymentSplitBar({ segments }: { segments: PaymentSegment[] }) {
  const grandTotal = segments.reduce((sum, s) => sum + s.netTotal, 0);
  if (grandTotal <= 0) {
    return null;
  }

  return (
    // Segments are focusable buttons, so the container is a group (role="img"
    // would hide them from assistive tech). End-cap rounding lives on the
    // first/last segments rather than an overflow-hidden container, which
    // would clip their focus rings.
    <div
      role="group"
      aria-label="Payment split by product"
      className="mt-2 flex h-2 w-full gap-[2px]"
    >
      {segments.map((segment) => {
        const share = (segment.netTotal / grandTotal) * 100;
        const shareLabel = `${share < 1 ? "<1" : Math.round(share)}%`;
        return (
          <TooltipProvider key={segment.key} delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* min-w keeps slivers hoverable (hit target ≥ mark). */}
                <button
                  type="button"
                  aria-label={`${segment.name}: ${segment.amountLabel} (${shareLabel})`}
                  className="h-full min-w-[6px] cursor-default first:rounded-l-full last:rounded-r-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  style={{
                    width: `${share}%`,
                    backgroundColor: segment.color,
                  }}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">
                  <span className="font-medium">{segment.name}</span>
                  {" · "}
                  {segment.amountLabel}
                  {" · "}
                  {shareLabel}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  PURCHASE: "Purchase",
  RESCHEDULE_FEE: "Reschedule fee",
  NO_SHOW_FEE: "No-show fee",
  LATE_CANCEL_FEE: "Late-cancel fee",
};

// Eye whose tooltip splits a product's net payments by type (purchase /
// reschedule / no-show / late-cancel fee). Same Eye + hover interaction as
// every other Eye in the card; rendered on every paid product row.
function PaymentBreakdownTooltip({
  breakdown,
}: {
  breakdown: ProductPaymentBreakdownEntry[];
}) {
  const { open, triggerProps, contentProps } = useHoverOpen();

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="View payment breakdown by type"
            className="inline-flex shrink-0 cursor-pointer"
            {...triggerProps}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-sm" {...contentProps}>
          <div className="space-y-0.5 py-1 text-xs">
            {breakdown.map((entry) => (
              <div
                key={`${entry.type}|${entry.currency}`}
                className="flex items-center justify-between gap-4"
              >
                <span>{PAYMENT_TYPE_LABELS[entry.type]}</span>
                <span className="font-medium">
                  {formatMajorUnits(entry.currency, entry.net)}
                </span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Eye whose tooltip lists the per-product refund breakdown (product name,
// amount, full/partial, status, initiated date). Always rendered — no refunds
// says so — and same hover interaction as every other Eye in the card.
function ProductRefundsTooltip({ products }: { products: ProductSummary[] }) {
  const { open, triggerProps, contentProps } = useHoverOpen();
  const refunded = products.filter((product) => product.refunds.length > 0);

  const refundLabel = (refund: ProductRefund) => {
    const status = refund.refundStatus
      ? REFUND_STATUS_LABELS[refund.refundStatus] ?? refund.refundStatus
      : null;
    const initiated = refund.refundInitiatedAt
      ? format(new Date(refund.refundInitiatedAt), "MMM d, yyyy")
      : null;
    return [
      formatMinorUnits(refund.currency, refund.amountRefunded),
      refund.amountRefunded >= refund.amount ? "Full" : "Partial",
      status && `Status: ${status}`,
      initiated && `Initiated: ${initiated}`,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="View refund details"
            className="inline-flex shrink-0 cursor-pointer"
            {...triggerProps}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-sm" {...contentProps}>
          {refunded.length === 0 ? (
            <p className="text-xs py-1">No refunds recorded</p>
          ) : (
            <div className="space-y-2 py-1">
              {refunded.map((product) => (
                <div key={product.productKey} className="text-xs">
                  <p className="font-medium">{product.productName}</p>
                  <div className="pl-2 text-[11px] text-muted-foreground space-y-0.5">
                    {product.refunds.map((refund, index) => (
                      <div key={index}>{refundLabel(refund)}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// "Aug 21, 2026" for a single held session; "Aug 2 – Aug 21, 2026" for a
// same-year span (year stated once); full dates on both sides across years.
function formatSessionRange(firstAt: string, lastAt: string): string {
  const first = new Date(firstAt);
  const last = new Date(lastAt);
  if (format(first, "yyyy-MM-dd") === format(last, "yyyy-MM-dd")) {
    return format(last, "MMM d, yyyy");
  }
  if (first.getFullYear() === last.getFullYear()) {
    return `${format(first, "MMM d")} – ${format(last, "MMM d, yyyy")}`;
  }
  return `${format(first, "MMM d, yyyy")} – ${format(last, "MMM d, yyyy")}`;
}

// Eye icon whose tooltip lists the product's past professionals (name ·
// session count · first–last session range). Always rendered — products with
// no history say so — to keep every row's layout identical. Controlled open
// state (useHoverOpen): stays open while the cursor is over the icon or the
// popover box, closes shortly after leaving both; click toggles.
function PastProfessionalsTooltip({
  professionals,
}: {
  professionals: CompletedSessionProfessional[];
}) {
  const { open, triggerProps, contentProps } = useHoverOpen();

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="View past professionals"
            className="inline-flex shrink-0 cursor-pointer"
            {...triggerProps}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-sm" {...contentProps}>
          {professionals.length === 0 ? (
            <p className="text-xs py-1">No previous professionals</p>
          ) : (
            <div className="space-y-1 py-1">
              <p className="text-xs font-medium">
                Past professionals ({professionals.length})
              </p>
              {professionals.map((professional) => (
                <div
                  key={professional.key}
                  className="text-xs text-muted-foreground"
                >
                  {professional.name}
                  {" · "}
                  {professional.sessionCount}
                  {professional.sessionCount === 1 ? " session" : " sessions"}
                  {" · "}
                  {formatSessionRange(
                    professional.firstSessionAt,
                    professional.lastSessionAt,
                  )}
                </div>
              ))}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Eye icon whose tooltip lists the sessions behind a count (date ·
// professional · bundle · how it was recorded). Same hover behavior as
// PastProfessionalsTooltip (useHoverOpen).
function SessionEntriesTooltip({
  entries,
}: {
  entries: RescheduleExceptionEntry[];
}) {
  const { open, triggerProps, contentProps } = useHoverOpen();

  if (entries.length === 0) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="View session details"
            className="inline-flex shrink-0 cursor-pointer"
            {...triggerProps}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-sm" {...contentProps}>
          <div className="space-y-2 py-1">
            {entries.map((entry, index) => (
              <div key={entry.session.id}>
                {index > 0 && <div className="border-t my-1.5 border-white/20" />}
                <p className="text-xs font-medium">
                  {format(
                    new Date(entry.session.start_time),
                    "MMM d, yyyy h:mm a",
                  )}
                </p>
                <div className="text-xs text-muted-foreground">
                  {entry.session.professional?.name
                    ? `${entry.session.professional.name} · `
                    : ""}
                  {entry.bundleName}
                  {" · "}
                  {entry.source === "Unsuccessful"
                    ? "Marked unsuccessful"
                    : entry.source === "No-show"
                      ? "No-show"
                      : "Cancelled"}
                </div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Every full-height tile shows at most this many rows (fixed — no per-page
// selector, per RH-712), so no single section can stretch the card: the grid
// row height is driven by the fixed 3 × 40px = 120px region rather than by
// whichever user has 12 products.
const ROWS_PER_PAGE = 3;

interface PagerState {
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
  onPageChange: (page: number) => void;
}

/**
 * Slices a tile's rows to one fixed-size page. The page is clamped on read
 * instead of reset in an effect, so a shrinking row list can never leave the
 * tile on a page that no longer exists. `pager` is null while the rows fit on
 * one page — short tiles render no chrome.
 */
function usePagedRows<T>(
  rows: T[],
  rowsPerPage: number = ROWS_PER_PAGE,
): { visible: T[]; pager: PagerState | null } {
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * rowsPerPage;
  const visible = rows.slice(start, start + rowsPerPage);

  return {
    visible,
    pager:
      rows.length > rowsPerPage
        ? {
            page: currentPage,
            pageCount,
            from: start + 1,
            to: start + visible.length,
            total: rows.length,
            onPageChange: setPage,
          }
        : null,
  };
}

function TilePager({ pager, label }: { pager: PagerState; label: string }) {
  // mt-auto pins the pager to the bottom of its tile, so all four tiles line
  // their controls up regardless of how tall their rows are.
  return (
    <div className="mt-auto border-t border-[color:var(--tile-divider)] pt-2 flex items-center justify-between gap-2">
      <span className="text-xs text-[color:var(--tile-label)]">
        {pager.from}–{pager.to} of {pager.total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`Previous ${label}`}
          disabled={pager.page === 0}
          onClick={() => pager.onPageChange(pager.page - 1)}
          className="p-0.5 rounded hover:bg-[color:var(--tile-divider)] disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={`Next ${label}`}
          disabled={pager.page >= pager.pageCount - 1}
          onClick={() => pager.onPageChange(pager.page + 1)}
          className="p-0.5 rounded hover:bg-[color:var(--tile-divider)] disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function TileRow({
  label,
  sublabel,
  value,
  info,
  markerColor,
  dense,
}: {
  label: string;
  sublabel?: string | null;
  value: React.ReactNode;
  info?: React.ReactNode;
  // Legend dot tying the row to its segment in a split bar (identity is
  // carried by the dot + label pair, never color alone).
  markerColor?: string;
  // 36px slot (strictly 2 per 72px region) for the stacked pair; sublabel is
  // dropped.
  dense?: boolean;
}) {
  return (
    // Fixed slot (40px, or 36px dense) with the content vertically centered,
    // so row height never depends on its content. No per-row separators —
    // the rows region draws one line on top and one above the pager.
    <div
      className={`${dense ? "h-[36px]" : "h-[40px]"} flex items-center justify-between gap-2`}
    >
      <div className="min-w-0">
        <div
          className={`${dense ? "text-xs" : "text-sm"} font-semibold flex items-center gap-1.5`}
        >
          {markerColor && (
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: markerColor }}
            />
          )}
          <span className="truncate">{label}</span>
        </div>
        {!dense && sublabel && (
          <div className="text-xs text-[color:var(--tile-label)] truncate">{sublabel}</div>
        )}
      </div>
      <div
        className={`flex items-center gap-1.5 ${dense ? "text-xs" : "text-sm"} font-medium shrink-0`}
      >
        {value}
        {info}
      </div>
    </div>
  );
}

// memo: the drawer memoizes all of these props per user payload, so drawer
// re-renders (cancel dialog, toasts) skip this card; it re-renders only for
// its own pager/tooltip state or a fresh user fetch.
export const UserSummaryCardRedesigned = memo(function UserSummaryCardRedesigned({
  productSummaries,
  sessionsSummary,
  paymentSummary,
  closestToExpiry,
  exceptionCounts,
  rhReschedules,
  noShows,
}: UserSummaryCardRedesignedProps) {
  const totalPayment = Object.values(paymentSummary).reduce(
    (sum, amount) => sum + amount,
    0,
  );
  const currency = Object.keys(paymentSummary)[0] || "INR";

  const { counts, logsByType } = exceptionCounts;
  const bundleExtensions = counts[ExceptionType.EXPIRY_EXTENSION] ?? 0;
  const freeSessions = counts[ExceptionType.EXTRA_SESSION] ?? 0;
  const manualAdjustments = counts[ExceptionType.MANUAL_ADJUSTMENT] ?? 0;
  const systemOverrides = counts[ExceptionType.SYSTEM_OVERRIDE] ?? 0;
  // RESCHEDULE rows are written by the server only for sessions cancelled or
  // marked unsuccessful with the exact "Reschedule exception" reason (a
  // granted favour) — routine reschedules produce no log. Sessions marked
  // before that writer went live have no rows and are not counted here.
  const rescheduleExceptions = counts[ExceptionType.RESCHEDULE] ?? 0;
  const totalExceptions =
    bundleExtensions +
    freeSessions +
    rescheduleExceptions +
    manualAdjustments +
    systemOverrides;

  const rhGroups = groupRhReschedulesByProfessional(rhReschedules);

  const exceptionInfo = (type: ExceptionType) => (
    <ExceptionTooltipContent
      exceptionLogs={logsByType[type] ?? []}
      side="left"
      trigger={<Eye className="h-3.5 w-3.5 cursor-pointer shrink-0" />}
    />
  );

  // Exception lines are heterogeneous, so they're flattened into rows first and
  // paged like every other tile.
  const exceptionRows = [
    {
      label: "Bundle extensions",
      count: bundleExtensions,
      info: exceptionInfo(ExceptionType.EXPIRY_EXTENSION),
    },
    {
      label: "Free sessions",
      count: freeSessions,
      info: exceptionInfo(ExceptionType.EXTRA_SESSION),
    },
    {
      label: "Reschedule exceptions",
      count: rescheduleExceptions,
      info: exceptionInfo(ExceptionType.RESCHEDULE),
    },
    {
      label: "Manual adjustments",
      count: manualAdjustments,
      info: exceptionInfo(ExceptionType.MANUAL_ADJUSTMENT),
    },
    {
      label: "System overrides",
      count: systemOverrides,
      info: exceptionInfo(ExceptionType.SYSTEM_OVERRIDE),
    },
  ].filter((row) => row.count > 0);

  const paidProducts = productSummaries.filter(
    (product) => product.payments.length > 0,
  );

  const paymentColorByKey = assignPaymentColors(paidProducts);

  const paymentSegments: PaymentSegment[] = [];
  for (const product of paidProducts) {
    const netTotal = product.payments.reduce(
      (sum, payment) => sum + payment.net,
      0,
    );
    if (netTotal <= 0) continue;

    paymentSegments.push({
      key: product.productKey,
      name: product.productName,
      color: paymentColorByKey.get(product.productKey)!,
      netTotal,
      amountLabel: product.payments
        .map((payment) => formatMajorUnits(payment.currency, payment.net))
        .join(" + "),
    });
  }

  // Total refunded per currency (paise) for the fixed "Refunded" row.
  const refundTotalsByCurrency = new Map<string, number>();
  for (const product of productSummaries) {
    for (const refund of product.refunds) {
      refundTotalsByCurrency.set(
        refund.currency,
        (refundTotalsByCurrency.get(refund.currency) ?? 0) +
          refund.amountRefunded,
      );
    }
  }
  const refundedTotalLabel =
    refundTotalsByCurrency.size > 0
      ? `−${[...refundTotalsByCurrency.entries()]
          .map(([cur, amount]) => formatMinorUnits(cur, amount))
          .join(" + ")}`
      : "—";

  // Sessions utilised fits one extra row (4) — its rows are shorter than
  // Payments' (no split bar / Refunded line), leaving slack below the 3rd.
  const sessionPages = usePagedRows(productSummaries, 4);
  // Payments shows 3 product rows — the 4th slot is the fixed Refunded row.
  const paymentPages = usePagedRows(paidProducts, 3);
  const exceptionPages = usePagedRows(exceptionRows);
  // The stacked half-height pair shows 2 fixed slots per page.
  const rhPages = usePagedRows(rhGroups, 2);
  const noShowPages = usePagedRows(noShows, 2);
  const totalNoShows = noShows.reduce(
    (sum, group) => sum + group.entries.length,
    0,
  );

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 rounded-xl p-2">
      <Tile
        title="Sessions utilised"
        headline={`${sessionsSummary.used}/${sessionsSummary.total}`}
        themeClassName={TILE_THEME.sessions}
      >
        {closestToExpiry &&
          closestToExpiry.daysUntilExpiry <= 7 &&
          closestToExpiry.remaining > 0 && (
            <div className="text-xs text-[color:var(--tile-label)] mt-1">
              {closestToExpiry.remaining}{" "}
              {closestToExpiry.remaining === 1 ? "bundle" : "bundles"} will
              expire in {closestToExpiry.daysUntilExpiry}{" "}
              {closestToExpiry.daysUntilExpiry === 1 ? "day" : "days"}
            </div>
          )}
        {/* Fixed geometry: rows are 40px slots and min-h covers that region
            + pager, so pages with 1 to 4 rows all render the tile at the
            same height with the pager pinned in the same spot. */}
        {/* 4 × 40px slots + pager — one more row than the sibling tiles. */}
        <div className="mt-2 border-t border-[color:var(--tile-divider)] flex flex-col flex-1 min-h-[196px]">
          {sessionPages.visible.map((product) => (
            <TileRow
              key={product.productKey}
              label={product.productName}
              sublabel={product.currentProfessionalName}
              value={
                <span className="flex items-center gap-1.5">
                  {/* Count only when some pack in the group still counts;
                      a group that is entirely refunded / transferred /
                      converted would otherwise read as a meaningless 0/0. */}
                  {product.hasCountingUsage &&
                    `${product.used}/${product.total}`}
                  {/* Status pill whenever a pack was excluded from the
                      count. In a mixed group it sits beside the count so
                      the total dropping vs the pack list is explained
                      rather than silent; the title spells out that it
                      refers to excluded packs, not the live one. */}
                  {product.nonCountingStatusLabels.length > 0 && (
                    <span
                      title={
                        product.hasCountingUsage
                          ? `Excludes ${product.nonCountingStatusLabels
                              .join(" / ")
                              .toLowerCase()} pack(s) — their sessions are not counted`
                          : undefined
                      }
                      className="rounded-full border border-[color:var(--tile-divider)] px-2 py-0.5 text-xs font-medium text-[color:var(--tile-label)]"
                    >
                      {product.nonCountingStatusLabels.join(" · ")}
                    </span>
                  )}
                </span>
              }
              info={
                <PastProfessionalsTooltip
                  professionals={product.pastProfessionals}
                />
              }
            />
          ))}
          {sessionPages.pager && (
            <TilePager pager={sessionPages.pager} label="products" />
          )}
        </div>
      </Tile>

      <Tile
        title="Payments"
        themeClassName={TILE_THEME.payments}
        headline={
          <>
            <span>{formatMajorUnits(currency, totalPayment)}</span>
            {/* The headline is NET — refunds are already subtracted upstream
                (calculatePaymentSummary), so the Refunded row below is
                informational, not a further deduction. */}
            <span className="text-xs font-normal text-[color:var(--tile-label)]">
              After refund deduction
            </span>
          </>
        }
      >
        <PaymentSplitBar segments={paymentSegments} />
        {/* Fixed geometry: 3 × 40px product rows + the fixed 40px Refunded
            row = 160px, min-h covers that + pager — 196px region, same
            page-flip stability as the sibling tiles. */}
        <div className="mt-2 border-t border-[color:var(--tile-divider)] flex flex-col flex-1 min-h-[196px]">
          {paymentPages.visible.map((product) => (
            <TileRow
              key={product.productKey}
              label={product.productName}
              markerColor={paymentColorByKey.get(product.productKey)}
              value={product.payments
                .map((payment) => formatMajorUnits(payment.currency, payment.net))
                .join(" + ")}
              info={
                product.paymentBreakdown.length > 0 ? (
                  <PaymentBreakdownTooltip
                    breakdown={product.paymentBreakdown}
                  />
                ) : undefined
              }
            />
          ))}
          {/* Block wrapper pins pager + Refunded row together at the tile
              bottom (and neutralizes the pager's own mt-auto). */}
          <div className="mt-auto">
            {paymentPages.pager && (
              <TilePager pager={paymentPages.pager} label="products" />
            )}
            <div className="border-t border-[color:var(--tile-divider)]">
              <TileRow
                label="Refunded"
                value={
                  refundTotalsByCurrency.size > 0 ? (
                    <span className={REFUND_AMOUNT_CLASS}>
                      {refundedTotalLabel}
                    </span>
                  ) : (
                    refundedTotalLabel
                  )
                }
                info={<ProductRefundsTooltip products={productSummaries} />}
              />
            </div>
          </div>
        </div>
      </Tile>

      <Tile
        title="Exceptions"
        headline={totalExceptions}
        themeClassName={TILE_THEME.neutral}
      >
        {/* Fixed geometry: rows are 40px slots (3 × 40 = 120px region) and
            min-h covers that region + pager, so pages with 1 to 3 rows all
            render the tile at the same height with the pager pinned in the
            same spot. */}
        <div className="mt-2 border-t border-[color:var(--tile-divider)] flex flex-col flex-1 min-h-[156px]">
          {exceptionPages.visible.map((row) => (
            <TileRow
              key={row.label}
              label={row.label}
              value={row.count}
              info={row.info}
            />
          ))}
          {exceptionPages.pager && (
            <TilePager pager={exceptionPages.pager} label="exception types" />
          )}
          {totalExceptions === 0 && (
            <div className="h-[40px] flex items-center text-xs text-[color:var(--tile-label)]">
              No exceptions granted
            </div>
          )}
        </div>
      </Tile>

      {/* Two separate stacked cards (mock layout): RH <24hrs on top,
          No-shows below — each a fixed 72px rows region of 2 × 36px dense
          rows, individually paginated. */}
      {/* gap-2 matches the grid gap so the stacked pair's seam lines up with
          the horizontal gutters. */}
      <div className="flex flex-col gap-2">
        <Tile
          compact
          title="Rescheduled by RH <24hrs"
          headline={rhReschedules.length}
          themeClassName={TILE_THEME.neutral}
        >
          {/* Strict 72px region (2 × 36px slots); pager space is NOT
              pre-reserved — it appends only when there are >2 items, keeping
              the slim cards from stretching the whole grid row. */}
          <div className="mt-2 border-t border-[color:var(--tile-divider)] flex flex-col flex-1 min-h-[72px]">
            <div className="h-[72px] flex flex-col">
              {rhPages.visible.map((group) => (
                <TileRow
                  key={group.professionalName}
                  dense
                  label={group.professionalName}
                  value={group.entries.length}
                  info={<SessionEntriesTooltip entries={group.entries} />}
                />
              ))}
              {rhReschedules.length === 0 && (
                <div className="h-[36px] flex items-center text-xs text-[color:var(--tile-label)]">
                  No RH-initiated reschedules
                </div>
              )}
            </div>
            {rhPages.pager && (
              <TilePager pager={rhPages.pager} label="professionals" />
            )}
          </div>
        </Tile>

        <Tile
          compact
          title="No-shows"
          headline={totalNoShows}
          themeClassName={TILE_THEME.neutral}
        >
          {/* Strict 72px region (2 × 36px slots); pager space is NOT
              pre-reserved — it appends only when there are >2 items, keeping
              the slim cards from stretching the whole grid row. */}
          <div className="mt-2 border-t border-[color:var(--tile-divider)] flex flex-col flex-1 min-h-[72px]">
            <div className="h-[72px] flex flex-col">
              {noShowPages.visible.map((group) => (
                <TileRow
                  key={group.productName}
                  dense
                  label={group.productName}
                  value={group.entries.length}
                  info={<SessionEntriesTooltip entries={group.entries} />}
                />
              ))}
              {totalNoShows === 0 && (
                <div className="h-[36px] flex items-center text-xs text-[color:var(--tile-label)]">
                  No no-shows recorded
                </div>
              )}
            </div>
            {noShowPages.pager && (
              <TilePager pager={noShowPages.pager} label="products" />
            )}
          </div>
        </Tile>
      </div>
    </div>
  );
});
